import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Loader2, CheckCircle2, Clock, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { errText, PasswordInput } from "@/components/control/kit";
import { PublicShell, publicInput, publicButton, publicLabel } from "@/components/control/PublicShell";

const TOKEN = /^[A-Za-z0-9_-]{20,200}$/;
const NOT_VALID = { expired: false, text: "Dieser Link ist nicht gültig." };

// New links carry the token in the fragment (#token=, never sent to a server); the older ?token= format is read once.
function readToken() {
  if (typeof window === "undefined") return "";
  const token = new URLSearchParams(window.location.hash.slice(1)).get("token")
    || new URLSearchParams(window.location.search).get("token") || "";
  return TOKEN.test(token) ? token : "";
}

const linkIssue = (err) => ({ expired: err.response?.status === 410, text: errText(err) });

function LinkProblem({ expired, text }) {
  const Icon = expired ? Clock : XCircle;
  return (
    <div role="alert" data-testid="reset-link-invalid" data-state={expired ? "abgelaufen" : "ungueltig"} className="space-y-4">
      <p data-testid={expired ? "reset-link-expired" : "reset-link-not-valid"} className="flex gap-2 rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm text-orange-800">
        <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
        <span>{text} Ein Link ist 30 Minuten gültig und nur einmal verwendbar.</span>
      </p>
      <Link to="/passwort-vergessen" data-testid="request-new-link" className={publicButton}>Neuen Link anfordern</Link>
    </div>
  );
}

function Done() {
  return (
    <div role="status" data-testid="reset-password-done" className="space-y-4">
      <p className="flex gap-2 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">
        <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden="true" />
        <span>Dein Passwort wurde geändert. Alle bisherigen Sitzungen wurden beendet. Deine Zwei-Faktor-Anmeldung bleibt unverändert.</span>
      </p>
    </div>
  );
}

function CheckFailed({ text, onRetry }) {
  return (
    <div role="alert" data-testid="reset-link-check-failed" className="space-y-4">
      <p className="text-sm text-red-700">{text}</p>
      <button type="button" onClick={onRetry} data-testid="reset-link-retry" className={publicButton}>Erneut prüfen</button>
    </div>
  );
}

function ResetForm({ token, onDone, onLinkIssue }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 10) { setError("Das Passwort muss mindestens 10 Zeichen lang sein."); return; }
    if (pw !== pw2) { setError("Die beiden Passwörter stimmen nicht überein."); return; }
    setBusy(true);
    setError(null);
    try {
      await api.post("/auth/password-reset/confirm", { token, password: pw });
      onDone();
    } catch (err) {
      if ([404, 410].includes(err.response?.status)) onLinkIssue(linkIssue(err));
      else setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" noValidate aria-busy={busy}>
      <label htmlFor="new-pw" className={publicLabel}>Neues Passwort</label>
      <PasswordInput id="new-pw" autoComplete="new-password" value={pw} onChange={setPw} testId="reset-password-new" className={publicInput} strength />
      <label htmlFor="new-pw2" className={publicLabel}>Passwort wiederholen</label>
      <PasswordInput id="new-pw2" autoComplete="new-password" value={pw2} onChange={setPw2} testId="reset-password-repeat" className={publicInput} />
      {error && <p role="alert" data-testid="reset-password-error" className="text-sm text-red-700">{error}</p>}
      <button type="submit" disabled={busy} data-testid="reset-password-submit" className={publicButton}>
        {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />} {busy ? "Wird gespeichert …" : "Passwort speichern"}
      </button>
    </form>
  );
}

export default function ResetPassword() {
  const [token] = useState(readToken);
  const [state, setState] = useState(token ? "checking" : "invalid");
  const [issue, setIssue] = useState(NOT_VALID);
  const [checkError, setCheckError] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // Remove the token from address bar and history right away; no Referer leaves this page.
    if (window.location.hash || window.location.search) window.history.replaceState(null, "", window.location.pathname);
    const meta = document.createElement("meta");
    meta.name = "referrer"; meta.content = "no-referrer";
    document.head.appendChild(meta);
    return () => { meta.remove(); };
  }, []);

  useEffect(() => {
    if (!token) { return; }
    api.post("/auth/password-reset/check", { token })
      .then(() => setState("form"))
      .catch((err) => {
        if ([404, 410].includes(err.response?.status)) { setIssue(linkIssue(err)); setState("invalid"); }
        else { setCheckError(errText(err)); setState("check-failed"); }
      });
  }, [token, attempt]);

  const showIssue = (next) => { setIssue(next); setState("invalid"); };
  const retry = () => { setState("checking"); setAttempt((n) => n + 1); };

  return (
    <PublicShell title="Neues Passwort festlegen" subtitle="Lege ein neues persönliches Passwort fest (mindestens 10 Zeichen). Alle bestehenden Sitzungen werden beendet, die Zwei-Faktor-Anmeldung bleibt bestehen." testId="reset-password-page">
      {state === "checking" && (
        <p role="status" data-testid="reset-link-checking" className="flex items-center gap-2 text-sm text-zinc-700">
          <Loader2 className="h-5 w-5 animate-spin text-brand-magenta" aria-hidden="true" /> Link wird geprüft …
        </p>
      )}
      {state === "invalid" && <LinkProblem {...issue} />}
      {state === "check-failed" && <CheckFailed text={checkError} onRetry={retry} />}
      {state === "done" && <Done />}
      {state === "form" && <ResetForm token={token} onDone={() => setState("done")} onLinkIssue={showIssue} />}
    </PublicShell>
  );
}
